// src/componentes/PagoEvidenciaModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
  listAll,
  deleteObject,
} from "firebase/storage";
import notify from "./common/notify";
import { dialogService } from "./common/dialogService";

/**
 * Modal para gestionar evidencias de un pago:
 * - Lista archivos en: `${basePath}/${deudaId}/pagos/${pagoId}/`
 * - Sube múltiples archivos con progreso
 * - Borra archivos individuales
 *
 * Props:
 * - deudaId: string (obligatorio)
 * - pagoId: string (obligatorio)
 * - onClose: () => void (obligatorio)
 * - basePath?: 'deudas' | 'cxp_deudas' (default: 'deudas')
 * - title?: string (encabezado opcional)
 * - onChange?: () => void (callback cuando se sube/borra algo)
 */
export default function PagoEvidenciaModal({
  deudaId,
  pagoId,
  onClose,
  basePath = "deudas",
  title = "Evidencias del pago",
  onChange,
}) {
  const storage = useMemo(() => getStorage(), []);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progressMap, setProgressMap] = useState({}); // filename -> 0..100
  const fileInputRef = useRef(null);

  const folderRef = useMemo(
    () => ref(storage, `${basePath}/${deudaId}/pagos/${pagoId}/`),
    [storage, basePath, deudaId, pagoId]
  );

  const listar = async () => {
    setBusy(true);
    try {
      const res = await listAll(folderRef);
      const rows = await Promise.all(
        res.items.map(async (it) => ({
          name: it.name,
          url: await getDownloadURL(it),
          _ref: it,
        }))
      );
      // Orden alfabético ascendente
      rows.sort((a, b) => a.name.localeCompare(b.name));
      setItems(rows);
    } catch (err) {
      console.error("Error listando evidencias:", err);
      notify.error("No se pudieron listar las evidencias.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    listar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderRef]);

  // Cerrar con ESC
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const onBackdropClick = (e) => {
    if (e.target.classList.contains("pem-backdrop")) onClose?.();
  };

  const safeName = (name) =>
    name.normalize("NFKD").replace(/[^\w.\- ]+/g, "_");

  const subirArchivos = async (files) => {
    if (!files || !files.length) return;
    setBusy(true);

    try {
      // Subimos uno por uno para manejar progreso y colisiones de nombre
      for (const file of files) {
        const clean = safeName(file.name);
        // Intento de evitar colisiones: si ya existe nombre, anteponer timestamp
        // No hay API directa para saber si existe; probamos a subir y si falla por conflicto
        // mejor usamos siempre prefijo timestamp para simplicidad (y evitar sobrescribir)
        const stampedRef = ref(
          folderRef,
          `${Date.now()}_${Math.floor(Math.random() * 1e6)}_${clean}`
        );

        await new Promise((resolve, reject) => {
          const uploadTask = uploadBytesResumable(stampedRef, file);
          uploadTask.on(
            "state_changed",
            (snap) => {
              const p = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
              setProgressMap((prev) => ({ ...prev, [stampedRef.name]: p }));
            },
            (err) => reject(err),
            () => resolve()
          );
        });
      }

      await listar();
      onChange?.();
      // limpiar input
      if (fileInputRef.current) fileInputRef.current.value = "";
      setProgressMap({});
    } catch (err) {
      console.error("Error subiendo evidencias:", err);
      notify.error("No se pudieron subir algunos archivos.");
    } finally {
      setBusy(false);
    }
  };

  const onFileChange = async (e) => {
    const fl = e.target.files;
    await subirArchivos(fl);
  };

  const borrar = async (fileRef) => {
    if (!fileRef) return;
    if (!await dialogService.confirm("¿Eliminar archivo definitivamente?")) return;
    setBusy(true);
    try {
      await deleteObject(fileRef);
      await listar();
      onChange?.();
    } catch (err) {
      console.error("Error borrando evidencia:", err);
      notify.error("No se pudo borrar el archivo.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pem-backdrop" onClick={onBackdropClick} style={styles.backdrop}>
      <div className="pem-modal" style={styles.modal}>
        <div style={styles.header}>
          <h3 style={styles.title}>{title}</h3>
          <button onClick={onClose} style={styles.closeBtn} aria-label="Cerrar">✕</button>
        </div>

        <div style={styles.actionsRow}>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={onFileChange}
            disabled={busy}
            accept=".jpg,.jpeg,.png,.webp,.pdf,.heic,.gif"
          />
          {busy && <span style={styles.hint}>Procesando…</span>}
        </div>

        {/* Progreso */}
        {Object.keys(progressMap).length > 0 && (
          <div style={styles.progressBox}>
            {Object.entries(progressMap).map(([name, p]) => (
              <div key={name} style={styles.progressRow}>
                <div style={styles.progressName}>{name}</div>
                <div style={styles.progressBarOuter} aria-label={`Subiendo ${name}`}>
                  <div style={{ ...styles.progressBarInner, width: `${p}%` }} />
                </div>
                <div style={styles.progressPct}>{p}%</div>
              </div>
            ))}
          </div>
        )}

        {/* Lista */}
        <ul style={styles.list}>
          {items.map((a) => {
            const isImg = /\.(png|jpe?g|webp|gif|heic)$/i.test(a.name);
            const isPdf = /\.pdf$/i.test(a.name);
            return (
              <li key={a.name} style={styles.item}>
                <div style={styles.itemInfo}>
                  {isImg && (
                    <a href={a.url} target="_blank" rel="noreferrer" title="Ver imagen">
                      <img src={a.url} alt={a.name} style={styles.thumb} />
                    </a>
                  )}
                  {!isImg && (
                    <a href={a.url} target="_blank" rel="noreferrer" style={styles.fileLink}>
                      {isPdf ? "📄 PDF" : "📎 Archivo"} — {a.name}
                    </a>
                  )}
                </div>
                <div>
                  <button onClick={() => window.open(a.url, "_blank")} style={styles.openBtn}>
                    Abrir
                  </button>
                  <button onClick={() => borrar(a._ref)} style={styles.deleteBtn}>
                    Eliminar
                  </button>
                </div>
              </li>
            );
          })}
          {items.length === 0 && !busy && (
            <li style={styles.empty}>No hay evidencias aún.</li>
          )}
        </ul>

        <div style={styles.footer}>
          <button onClick={onClose} style={styles.primaryBtn}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

/* ——— estilos inline mínimos (si usas Tailwind o CSS propio, puedes quitarlos) ——— */
const styles = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
  },
  modal: {
    width: "min(680px, 94vw)",
    background: "#111827",
    color: "#F9FAFB",
    borderRadius: 16,
    padding: 16,
    boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
    border: "1px solid #374151",
    maxHeight: "86vh",
    overflow: "auto",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    justifyContent: "space-between",
    marginBottom: 8,
  },
  title: {
    fontSize: 18,
    margin: 0,
  },
  closeBtn: {
    background: "transparent",
    color: "#E5E7EB",
    border: "1px solid #374151",
    borderRadius: 8,
    padding: "6px 10px",
    cursor: "pointer",
  },
  actionsRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 12,
  },
  hint: { fontSize: 12, opacity: 0.8 },
  progressBox: {
    background: "#0b1220",
    border: "1px solid #1f2937",
    borderRadius: 12,
    padding: 8,
    marginBottom: 12,
  },
  progressRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 48px",
    gap: 8,
    alignItems: "center",
    marginBottom: 6,
  },
  progressName: { fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  progressBarOuter: { background: "#111827", border: "1px solid #374151", borderRadius: 8, height: 10, overflow: "hidden" },
  progressBarInner: { background: "#3B82F6", height: "100%" },
  progressPct: { fontSize: 12, textAlign: "right" },

  list: { listStyle: "none", padding: 0, margin: "6px 0 8px 0" },
  item: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    borderBottom: "1px solid #1f2937",
    padding: "8px 0",
  },
  itemInfo: { display: "flex", alignItems: "center", gap: 10 },
  thumb: { width: 48, height: 48, objectFit: "cover", borderRadius: 8, border: "1px solid #374151" },
  fileLink: { color: "#93C5FD", textDecoration: "none" },
  empty: { opacity: 0.8, fontSize: 14, padding: "12px 0" },

  footer: { display: "flex", justifyContent: "flex-end", marginTop: 6 },
  primaryBtn: {
    background: "#2563EB",
    color: "#fff",
    border: "none",
    padding: "8px 14px",
    borderRadius: 10,
    cursor: "pointer",
  },
  openBtn: {
    background: "transparent",
    color: "#93C5FD",
    border: "1px solid #1f2937",
    borderRadius: 8,
    padding: "6px 10px",
    cursor: "pointer",
    marginRight: 6,
  },
  deleteBtn: {
    background: "#ef4444",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "6px 10px",
    cursor: "pointer",
  },
};
